import { observer } from "mobx-react";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import FontBody3 from "../../../../../components/fonts/font-body3/font-body3";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import IconWarning from "../../../../../components/icons/icon-warning/icon-warning";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import bulkRmaActionStore from "../../stores/bulk-rma-action-store";
import { BACKGROUND_JOB_NOTICE_STATE } from "../../stores/background-job-notice-store";
import {
    getBulkActionNoticeProgress,
    getBulkActionNoticeState,
} from "./bulk-action-notice-helpers";
import "./bulk-action-notice-description.scss";

const DASHBOARD_RETURNS_SCOPE = "dashboard-returns-page";

const BulkActionNoticeDescription: React.FC = observer(() => {
    const { getTranslation } = useDashboardTranslation();
    const report = bulkRmaActionStore.newReport;

    if (!report) {
        return null;
    }

    const state = getBulkActionNoticeState(report);
    const { succeeded, total } = report.counts;

    if (state === BACKGROUND_JOB_NOTICE_STATE.IN_PROGRESS) {
        const { processed, percent, total: progressTotal } = getBulkActionNoticeProgress(
            report.counts,
        );
        return (
            <div className="bulk-action-notice-description bulk-action-notice-description--progress">
                <span className="bulk-action-notice-description__percent">
                    <FontBody3 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.SEMANTIC_SUCCESS}>
                        {percent}%
                    </FontBody3>
                </span>
                <FontBody2 color={FONT_COLOR.PRIMARY2}>
                    {getTranslation("BULK_ACTION_NOTICE_PROGRESS_COUNT", DASHBOARD_RETURNS_SCOPE, [
                        String(processed),
                        String(progressTotal),
                    ])}
                </FontBody2>
            </div>
        );
    }

    if (state === BACKGROUND_JOB_NOTICE_STATE.STOPPED) {
        return (
            <FontBody2 color={FONT_COLOR.PRIMARY2}>
                {getTranslation("BULK_ACTION_NOTICE_STOPPED_COUNT", DASHBOARD_RETURNS_SCOPE, [
                    String(succeeded),
                    String(total),
                ])}
            </FontBody2>
        );
    }

    const needReview = Math.max(0, report.counts.total - report.counts.succeeded);
    const markedDoneClause = getTranslation("BULK_ACTION_NOTICE_MARKED_DONE_CLAUSE", DASHBOARD_RETURNS_SCOPE);

    if (state === BACKGROUND_JOB_NOTICE_STATE.COMPLETE) {
        return (
            <FontBody2 color={FONT_COLOR.PRIMARY2}>
                <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
                    {String(succeeded)}
                </FontBody2>
                {markedDoneClause}
            </FontBody2>
        );
    }

    return (
        <div className="bulk-action-notice-description bulk-action-notice-description--partial">
            <FontBody2 color={FONT_COLOR.PRIMARY2}>
                <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
                    {String(succeeded)}
                </FontBody2>
                {markedDoneClause}
            </FontBody2>
            {needReview > 0 && (
                <div className="bulk-action-notice-description__still-open">
                    <IconWarning color="var(--color-accent-4)" />
                    <FontBody2 color={FONT_COLOR.PRIMARY2}>
                        <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
                            {String(needReview)}
                        </FontBody2>
                        {getTranslation("BULK_ACTION_NOTICE_STILL_OPEN_CLAUSE", DASHBOARD_RETURNS_SCOPE)}
                    </FontBody2>
                </div>
            )}
        </div>
    );
});

export default BulkActionNoticeDescription;
